import React from 'react';
import PropTypes from 'prop-types';
import { Currency } from '@reservamos/elements';
import useCurrencyConfig from 'hooks/whitelabel/useCurrencyConfig';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';

/**
 * LocalizedCurrency component.
 * Render a currency component with localized formatting configurations.
 * @param {object} props - price, currencyStyle, decimals, sign, locale, currency, color, fontFamily, size, mobileSize, weight, lineThrough, italic, hasHighlight, currencyLowercase, whiteSpace, showCurrencySuffix.
 * @param {number} props.price - The price value to display.
 * @param {string} props.currencyStyle - Currency formatting style that enables Intl mode when provided.
 * @param {number} props.decimals - The number of decimals to display (default: 2).
 * @param {string} props.sign - Defines currency sign.
 * @param {string} props.color - Color theme for the currency display.
 * @param {string} props.fontFamily - Font family to use.
 * @param {string} props.size - Font size.
 * @param {string} props.mobileSize - Font size for mobile.
 * @param {string} props.weight - Font weight.
 * @param {boolean} props.lineThrough - Sets the line-through decoration.
 * @param {boolean} props.italic - Sets font-style: italic.
 * @param {boolean} props.hasHighlight - Adds oval to price.
 * @param {boolean} props.currencyLowercase - Disables uppercase.
 * @param {string} props.whiteSpace - Set text white-space.
 * @param {boolean} props.showCurrencySuffix - If set, overrides SHOW_CURRENCY_SUFFIX for the currency code (e.g. PEN) after the amount.
 * @returns {JSX.Element} The rendered localized currency component.
 */
const LocalizedCurrency = ({
  price,
  currencyStyle,
  decimals,
  sign,
  color,
  fontFamily,
  size,
  mobileSize,
  weight,
  lineThrough,
  italic,
  hasHighlight,
  currencyLowercase,
  whiteSpace,
  showCurrencySuffix,
}) => {
  const { currency, locale, decimals: configDecimals } = useCurrencyConfig();
  const { SHOW_CURRENCY_SUFFIX } = useWhitelabelFeatures();

  const finalDecimals = decimals !== undefined ? decimals : configDecimals;
  const showCurrencyCode =
    showCurrencySuffix !== undefined ? showCurrencySuffix : SHOW_CURRENCY_SUFFIX;

  return (
    <Currency
      price={price}
      currencyStyle={currencyStyle}
      currency={currency}
      locale={locale}
      decimals={finalDecimals}
      sign={sign}
      color={color}
      fontFamily={fontFamily}
      size={size}
      mobileSize={mobileSize}
      weight={weight}
      lineThrough={lineThrough}
      italic={italic}
      hasHighlight={hasHighlight}
      currencyLowercase={currencyLowercase}
      whiteSpace={whiteSpace}
      showCurrency={showCurrencyCode}
    />
  );
};

LocalizedCurrency.propTypes = {
  price: PropTypes.number.isRequired,
  /** Intl style (enables Intl mode when provided) */
  currencyStyle: PropTypes.oneOf(['decimal', 'currency']),
  /** Defines how much decimals to show */
  decimals: PropTypes.number,
  /** Defines currency sign */
  sign: PropTypes.oneOf(['', '$', '€']),
  /** Color */
  color: PropTypes.oneOf([
    'primary',
    'primaryStrong',
    'accent',
    'grayStrong',
    'grayMedium',
    'grayLight',
    'info',
    'discount',
    'white',
    'error',
    'success',
    'loyaltyLight',
  ]),
  /** Font Family */
  fontFamily: PropTypes.oneOf(['primary', 'secondary']),
  /** Font size */
  size: PropTypes.oneOf(['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL']),
  /** Font size for mobile */
  mobileSize: PropTypes.oneOf(['', 'XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL']),
  /** Font weight */
  weight: PropTypes.oneOf(['bold', 'semibold', 'regular']),
  /** Sets the `line-through` decoration */
  lineThrough: PropTypes.bool,
  /** Sets `font-style: italic` */
  italic: PropTypes.bool,
  /** Adds oval to price */
  hasHighlight: PropTypes.bool,
  /** Disables uppercase */
  currencyLowercase: PropTypes.bool,
  /** Set text white-space */
  whiteSpace: PropTypes.oneOf(['', 'normal', 'nowrap', 'pre-line']),
  /** If set, overrides SHOW_CURRENCY_SUFFIX (currency code after amount, e.g. PEN) */
  showCurrencySuffix: PropTypes.bool,
};

export default LocalizedCurrency;
